/**
 * Unified Feature Registry
 * Single source of truth for all features in the system
 * Handles Roboflow auto-detection, user-defined features, and system features
 */

// Core feature registry with consistent snake_case IDs
export const FEATURE_REGISTRY = {
  // Architectural Features
  "arch": {
    id: "arch",
    label: "Arch",
    source: "roboflow",
    category: "architectural"
  },
  "ashlar wall": {
    id: "ashlar_wall", 
    label: "Ashlar Wall",
    source: "roboflow",
    category: "architectural"
  },
  "column": {
    id: "column",
    label: "Column", 
    source: "roboflow",
    category: "architectural"
  },
  "niche": {
    id: "niche",
    label: "Niche",
    source: "roboflow", 
    category: "architectural"
  },
  "polygonal wall": {
    id: "polygonal_wall",
    label: "Polygonal Wall",
    source: "roboflow",
    category: "architectural"
  },
  "polygonalMasonry": {
    id: "polygonal_masonry",
    label: "Polygonal Masonry", 
    source: "system",
    category: "architectural"
  },
  "pyramid": {
    id: "pyramid",
    label: "Pyramid",
    source: "roboflow",
    category: "architectural"
  },
  "terraces": {
    id: "terraces",
    label: "Terraces",
    source: "roboflow", 
    category: "architectural"
  },

  // Construction & Tool Marks
  "bevel block": {
    id: "bevel_block",
    label: "Bevel Block",
    source: "roboflow",
    category: "construction"
  },
  "clamps": {
    id: "clamps", 
    label: "Clamps",
    source: "roboflow",
    category: "construction"
  },
  "damage holes": {
    id: "damage_holes",
    label: "Damage Holes",
    source: "roboflow",
    category: "construction"
  },
  "lipped blocks": {
    id: "lipped_blocks",
    label: "Lipped Blocks", 
    source: "roboflow",
    category: "construction"
  },
  "nubs": {
    id: "nubs",
    label: "Nubs",
    source: "roboflow",
    category: "construction"
  },
  "pinchmark": {
    id: "pinch_mark",
    label: "Pinch Mark",
    source: "roboflow",
    category: "construction"
  },
  "scoop marks": {
    id: "scoop_marks",
    label: "Scoop Marks",
    source: "roboflow", 
    category: "construction"
  },
  "scrape marks": {
    id: "scrape_marks",
    label: "Scrape Marks",
    source: "roboflow",
    category: "construction"
  },
  "wedge holes": {
    id: "wedge_holes",
    label: "Wedge Holes",
    source: "roboflow",
    category: "construction"
  },

  // Cultural & Religious
  "hanan pacha": {
    id: "hanan_pacha",
    label: "Hanan Pacha",
    source: "roboflow",
    category: "cultural"
  },
  "hindu temple": {
    id: "hindu_temple", 
    label: "Hindu Temple",
    source: "roboflow",
    category: "cultural"
  },
  "lingam": {
    id: "lingam",
    label: "Lingam",
    source: "roboflow",
    category: "cultural"
  },
  "sarcophagus": {
    id: "sarcophagus",
    label: "Sarcophagus",
    source: "roboflow",
    category: "cultural"
  },

  // Artistic & Decorative
  "stairstepmotif": {
    id: "stairstep_motif",
    label: "Stairstep Motif",
    source: "roboflow", 
    category: "artistic"
  },

  // Natural & Environmental
  "vitrification": {
    id: "vitrification",
    label: "Vitrification",
    source: "roboflow",
    category: "natural"
  }
};

/**
 * Get feature by original name (from Roboflow or user input)
 */
export const getFeatureByName = (originalName) => {
  return FEATURE_REGISTRY[originalName] || null;
};

/**
 * Get feature by ID
 */
export const getFeatureById = (id) => {
  return Object.values(FEATURE_REGISTRY).find(feature => feature.id === id) || null;
};

/**
 * Convert original feature name to standardized ID
 */
export const getStandardFeatureId = (originalName) => {
  const feature = getFeatureByName(originalName);
  return feature ? feature.id : null;
};

/**
 * Get display label for a feature ID
 */
export const getFeatureLabel = (id) => {
  const feature = getFeatureById(id);
  return feature ? feature.label : id; // Fallback to ID if not found
};

/**
 * Add a new user-defined feature
 */
export const addUserFeature = (label, category, userId) => {
  const id = `user_${userId}_${Date.now()}`;
  const feature = {
    id,
    label,
    source: "user",
    category: category || "uncategorized",
    createdBy: userId,
    createdAt: new Date().toISOString()
  };
  
  FEATURE_REGISTRY[label] = feature;
  return feature;
};

/**
 * Get all features by category
 */
export const getFeaturesByCategory = (category) => {
  return Object.values(FEATURE_REGISTRY).filter(feature => feature.category === category);
};

/**
 * Get all feature categories
 */
export const getFeatureCategories = () => {
  const categories = new Set();
  Object.values(FEATURE_REGISTRY).forEach(feature => {
    categories.add(feature.category);
  });
  return Array.from(categories);
};

/**
 * Bulk convert feature names to IDs (for migration)
 */
export const convertFeatureNamesToIds = (featureNames) => {
  if (!Array.isArray(featureNames)) return [];
  
  return featureNames
    .map(name => getStandardFeatureId(name))
    .filter(id => id !== null); // Remove any unmapped features
};

/**
 * Bulk convert feature IDs to labels (for display)
 */
export const convertFeatureIdsToLabels = (featureIds) => {
  if (!Array.isArray(featureIds)) return [];
  
  return featureIds.map(id => getFeatureLabel(id));
};
